import React, { useState, useEffect, useCallback } from 'react';
import { dispatchAPI } from '@/lib/api';
import { getToday } from '@/lib/utils';
import { Truck, Package, MapPin, Check, AlertTriangle, ChevronRight, Calendar, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const LogisticaDispatchPage = () => {
  const [selectedDate, setSelectedDate] = useState(getToday());
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dispatchDialogOpen, setDispatchDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [dispatchQuantities, setDispatchQuantities] = useState({});
  const [dispatchNotes, setDispatchNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const loadPendingOrders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dispatchAPI.getPending(selectedDate);
      setPendingOrders(response.data);
    } catch (error) {
      toast.error('Error al cargar pedidos');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    loadPendingOrders();
  }, [loadPendingOrders]);

  const openDispatchDialog = (order) => {
    setSelectedOrder(order);
    
    // Si ya tiene despacho, cargar cantidades existentes
    if (order.dispatch) {
      const quantities = {};
      order.dispatch.items.forEach(item => {
        quantities[item.item_id] = item.dispatched_quantity;
      });
      setDispatchQuantities(quantities);
      setDispatchNotes(order.dispatch.notes || '');
    } else {
      // Inicializar con las cantidades pedidas
      const quantities = {};
      order.items.forEach(item => {
        quantities[item.item_id] = item.requested_quantity;
      });
      setDispatchQuantities(quantities);
      setDispatchNotes('');
    }
    
    setDispatchDialogOpen(true);
  };

  const handleQuantityChange = (itemId, value) => {
    setDispatchQuantities(prev => ({
      ...prev,
      [itemId]: parseFloat(value) || 0
    }));
  };

  const confirmDispatch = async () => {
    if (!selectedOrder) return;
    
    setSaving(true);
    try {
      const dispatchData = {
        order_id: selectedOrder.id,
        items: Object.entries(dispatchQuantities).map(([item_id, dispatched_quantity]) => ({
          item_id,
          dispatched_quantity
        })),
        notes: dispatchNotes
      };
      
      if (selectedOrder.has_dispatch) {
        await dispatchAPI.update(selectedOrder.dispatch.id, dispatchData);
        toast.success('Despacho actualizado');
      } else {
        await dispatchAPI.create(dispatchData);
        toast.success('Despacho registrado');
      }
      
      setDispatchDialogOpen(false);
      loadPendingOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar despacho');
    } finally {
      setSaving(false);
    }
  };

  const getTotalRequested = () => {
    if (!selectedOrder) return 0;
    return selectedOrder.items.reduce((sum, item) => sum + item.requested_quantity, 0);
  };

  const getTotalDispatched = () => {
    return Object.values(dispatchQuantities).reduce((sum, qty) => sum + qty, 0);
  };

  const getCompliancePercentage = () => {
    const requested = getTotalRequested();
    const dispatched = getTotalDispatched();
    if (requested === 0) return 100;
    return Math.round((dispatched / requested) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tupu-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="pb-24 md:pb-6" data-testid="logistica-dispatch-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Despachos del Día</h1>
        <p className="text-muted-foreground">Confirma las cantidades a enviar a cada local</p>
      </div>

      {/* Selector de fecha */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
        <div className="flex items-center gap-4">
          <Calendar className="w-5 h-5 text-slate-400" />
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-44"
          />
          <Button variant="outline" onClick={loadPendingOrders}>
            Actualizar
          </Button>
        </div>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Total Pedidos</p>
          <p className="text-2xl font-bold text-slate-900">{pendingOrders.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Pendientes</p>
          <p className="text-2xl font-bold text-amber-600">
            {pendingOrders.filter(o => !o.has_dispatch).length}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Despachados</p>
          <p className="text-2xl font-bold text-green-600">
            {pendingOrders.filter(o => o.has_dispatch).length}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Cumplimiento Prom.</p>
          <p className="text-2xl font-bold text-blue-600">
            {pendingOrders.filter(o => o.dispatch).length > 0
              ? Math.round(pendingOrders.filter(o => o.dispatch).reduce((sum, o) => sum + o.dispatch.compliance_percentage, 0) / pendingOrders.filter(o => o.dispatch).length)
              : 0}%
          </p>
        </div>
      </div>

      {/* Lista de pedidos */}
      <div className="space-y-4">
        {pendingOrders.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No hay pedidos para esta fecha</p>
          </div>
        ) : (
          pendingOrders.map((order, index) => (
            <motion.div
              key={order.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`bg-white rounded-xl border-2 p-4 cursor-pointer transition-all hover:shadow-md ${
                order.has_dispatch 
                  ? 'border-green-300 bg-green-50/30' 
                  : 'border-amber-300 bg-amber-50/30'
              }`}
              onClick={() => openDispatchDialog(order)}
              data-testid={`order-${order.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    order.has_dispatch ? 'bg-green-100' : 'bg-amber-100'
                  }`}>
                    {order.has_dispatch 
                      ? <CheckCircle2 className="w-6 h-6 text-green-600" />
                      : <Truck className="w-6 h-6 text-amber-600" />
                    }
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <h3 className="font-bold text-slate-900">{order.location_name}</h3>
                    </div>
                    <p className="text-sm text-slate-500">
                      {order.items.length} productos • {order.items.reduce((sum, i) => sum + i.requested_quantity, 0)} unidades
                    </p>
                    {order.maestro && (
                      <span className="text-xs text-primary font-medium">{order.maestro}</span>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  {order.has_dispatch && (
                    <div className="text-right">
                      <p className={`text-lg font-bold ${
                        order.dispatch.compliance_percentage >= 100 ? 'text-green-600' :
                        order.dispatch.compliance_percentage >= 80 ? 'text-amber-600' : 'text-red-600'
                      }`}>
                        {order.dispatch.compliance_percentage}%
                      </p>
                      <p className="text-xs text-slate-500">cumplimiento</p>
                    </div>
                  )}
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Dialog de Despacho */}
      <Dialog open={dispatchDialogOpen} onOpenChange={setDispatchDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-tupu-orange-500" />
              {selectedOrder?.has_dispatch ? 'Editar Despacho' : 'Confirmar Despacho'}
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              {selectedOrder?.location_name} • {selectedOrder?.maestro || 'General'}
            </p>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Resumen de cumplimiento */}
            <div className={`rounded-lg p-4 ${
              getCompliancePercentage() >= 100 ? 'bg-green-50 border border-green-200' :
              getCompliancePercentage() >= 80 ? 'bg-amber-50 border border-amber-200' : 
              'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Cumplimiento</span>
                <span className={`text-2xl font-bold ${
                  getCompliancePercentage() >= 100 ? 'text-green-600' :
                  getCompliancePercentage() >= 80 ? 'text-amber-600' : 'text-red-600'
                }`}>
                  {getCompliancePercentage()}%
                </span>
              </div>
              <div className="flex justify-between text-xs mt-2 text-slate-600">
                <span>Pedido: {getTotalRequested()} unidades</span>
                <span>Despachando: {getTotalDispatched()} unidades</span>
              </div>
            </div>

            {/* Lista de items */}
            <div className="space-y-3">
              {selectedOrder?.items.map((item) => {
                const dispatched = dispatchQuantities[item.item_id] || 0;
                const diff = dispatched - item.requested_quantity;
                
                return (
                  <div key={item.item_id} className="bg-slate-50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium text-slate-900">{item.item_name}</p>
                        <p className="text-xs text-slate-500">{item.item_code} • {item.unit}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-600">Pedido: <span className="font-bold">{item.requested_quantity}</span></p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-slate-600">Despachar:</span>
                      <Input
                        type="number"
                        inputMode="decimal"
                        step="0.1"
                        min="0"
                        value={dispatchQuantities[item.item_id] || ''}
                        onChange={(e) => handleQuantityChange(item.item_id, e.target.value)}
                        className="w-24 text-center font-bold"
                        data-testid={`dispatch-qty-${item.item_code}`}
                      />
                      <span className={`text-sm font-medium ${
                        diff === 0 ? 'text-green-600' :
                        diff > 0 ? 'text-blue-600' : 'text-red-600'
                      }`}>
                        {diff > 0 ? '+' : ''}{diff !== 0 ? diff : '✓'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Notas */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Observaciones (opcional)
              </label>
              <Textarea
                value={dispatchNotes}
                onChange={(e) => setDispatchNotes(e.target.value)}
                placeholder="Notas sobre el despacho..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDispatchDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={confirmDispatch} 
              disabled={saving}
              className="bg-tupu-orange-600 hover:bg-tupu-orange-700"
            >
              {saving ? 'Guardando...' : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  {selectedOrder?.has_dispatch ? 'Actualizar' : 'Confirmar Despacho'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LogisticaDispatchPage;
